import { FilterBar } from "@/components/FilterBar";
import { SearchIcon } from "@/components/icons";
import { MainContainer } from "@/components/mainContainer";
import * as Inputs from "../../../components/inputs";
import * as Fonts from "../../../components/texts";
import * as T from "../../../components/table";
import {
  Button,
  FormControl,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useCustomToast } from "@/hooks/useToast";
import { Pagination } from "@/components/pagination";
import { OutlineEditNote } from "@/components/icons/edit";
import { TrashOutline } from "@/components/icons/trash";
import {
  ParametrizationIndexCreateDto,
  ParametrizationIndexDto,
  ParametrizationIndexFilterDto,
} from "../types";
import { useParametrization } from "@/hooks/useParametrization";
import LoadingOverlay from "@/components/overlay";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { ParameterizationTypeEnum } from "../enums";

export const getTypeLabel = (value: string) => {
  if (value in ParameterizationTypeEnum) {
    return ParameterizationTypeEnum[
      value as keyof typeof ParameterizationTypeEnum
    ];
  }
  return value;
};

const initialFilter: ParametrizationIndexFilterDto = {
  name: "",
  type: "",
};

const schema = Joi.object({
  name: Joi.string().required().messages({
    "string.empty": "Nome deve ser informado",
  }),
  type: Joi.string().required().messages({
    "string.empty": "Tipo deve ser informado",
  }),
});

const types = [
  { value: "branch", label: "Unidade" },
  { value: "team", label: "Tipo do Pacote" },
  { value: "type", label: "Tipo do Evento" },
];

export const Parameterization = () => {
  const { customToast } = useCustomToast();
  const [page, setPage] = useState<number>(1);
  const {
    getParametrizations,
    parametrizationCreate,
    parametrizationDelete,
    parametrizationUpdate,
  } = useParametrization();
  const [parametrizations, setParametrization] = useState<
    ParametrizationIndexDto[]
  >([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [totalParametrization, setTotalParametrization] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [filter, setFilter] =
    useState<ParametrizationIndexFilterDto>(initialFilter);
  const [stopedTyping, setStopedTyping] = useState(false);
  const [typingTimer, setTypingTimer] = useState<NodeJS.Timeout | undefined>();
  const [selectedFilterField, setSelectedFilterField] =
    useState<string>("name");

  const handleInputChange = () => {
    clearTimeout(typingTimer);

    const newTypingTimer = setTimeout(() => {
      setStopedTyping(true);
    }, 500);

    setTypingTimer(newTypingTimer);
  };

  const filters = [
    {
      field: (
        <HStack w="30%" justifyContent="flex-end" key={1}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Buscar por:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => setSelectedFilterField(event.target.value)}
          >
            <option value="">Selecione</option>
            <option value="name">Nome</option>
            <option value="type">Tipo</option>
          </Select>
        </HStack>
      ),
    },
    {
      field: (
        <InputGroup alignItems="center" key={2}>
          <InputLeftElement pointerEvents="none" top="" left={1}>
            <SearchIcon />
          </InputLeftElement>
          <Inputs.SimpleText
            placeholder="O que você procura?"
            w="100%"
            h={9}
            borderRadius={4}
            textIndent="35px"
            bg="neutral.0"
            onChange={(event) => {
              setFilter((prev) => ({
                ...prev,
                [selectedFilterField]: event.target.value,
              }));
              handleInputChange();
            }}
          />
        </InputGroup>
      ),
    },
  ];

  const callbackParametrizations = useCallback(() => {
    setIsLoading(true);
    getParametrizations({ page, ...filter })
      .then((response) => {
        setParametrization(response.data.data),
          setTotalParametrization(response.data.total),
          setLastPage(response.data.lastPage);
        console.log(response.data.data);
      })
      .catch((error) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => setIsLoading(false));
  }, [page, filter]);

  useEffect(() => callbackParametrizations(), [page, filter.name, filter.type]);

  useEffect(() => {
    if (stopedTyping) {
      if (page === 1) {
        callbackParametrizations();
      } else {
        setPage(1);
      }
      setStopedTyping(false);
    }
  }, [filter.name, filter.type, stopedTyping]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ParametrizationIndexCreateDto>({
    resolver: joiResolver(schema),
  });

  const onSubmit = async (data: ParametrizationIndexCreateDto) => {
    setIsLoading(true);

    try {
      if (editingId !== null) {
        await parametrizationUpdate(editingId, data);
        setParametrization((prev) =>
          prev.map((item) =>
            item.id === editingId ? { ...item, ...data } : item
          )
        );
        customToast("success", "Parâmetro atualizado com sucesso");
        reset();
        setEditingId(null);
      } else {
        const response = await parametrizationCreate(data);
        setParametrization((prev) => [...prev, response.data]);
        customToast("success", "Parâmetro cadastrado com sucesso");
        reset();
      }
      onClose();
    } catch (error) {
      customToast("error", "Erro ao cadastrar Parâmetro");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await parametrizationDelete(id);
      setParametrization((prev) => prev.filter((item) => item.id !== id));
      customToast("success", "Parâmetro deletado com sucesso");
    } catch (error) {
      customToast("error", "Erro ao deletar parâmetro");
    }
  };

  const handleEdit = (param: ParametrizationIndexDto) => {
    setEditingId(param.id);
    setValue("name", param.name);
    setValue("type", param.type);
    onOpen();
  };

  return (
    <MainContainer
      height={isLoading || parametrizations.length < 9 ? undefined : "100%"}
      title="Configurações > Parametrizações"
    >
      <FilterBar
        title={`Parametrizações (${totalParametrization})`}
        buttons={[
          {
            title: "Novo Cadastro",
            onClick: onOpen,
          },
        ]}
        filters={filters}
      />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        closeOnEsc={false}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent
          left={142}
          bgColor="neutral.100"
          as="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <ModalHeader>Cadastro</ModalHeader>
          <ModalBody mb={6}>
            <Stack spacing={5}>
              <FormControl w="100%">
                <Fonts.FormLabelText>Tipo</Fonts.FormLabelText>
                <Select
                  w="100%"
                  h={10}
                  borderRadius={4}
                  bg="neutral.0"
                  {...register("type")}
                >
                  <option value="">Selecione</option>
                  {types.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
                {errors.type && <span>{errors.type.message}</span>}
              </FormControl>
              <FormControl w="100%">
                <Fonts.FormLabelText>Nome</Fonts.FormLabelText>
                <Input
                  type="text"
                  textIndent={"1px"}
                  w="100%"
                  h={10}
                  borderRadius={3}
                  border="1px solid #EAEAEA"
                  bgColor="neutral.0"
                  {...register("name")}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack w="100%" spacing={7}>
              <Button
                variant="outline"
                borderColor="secondary.500"
                colorScheme="secondary.500"
                w="100%"
                h={9}
                borderRadius={4}
                onClick={onClose}
                _hover={{
                  bgColor: "secondary.100",
                }}
                _active={{
                  bgColor: "secondary.300",
                }}
              >
                <Fonts.ButtonText color="secondary.500">
                  Cancelar
                </Fonts.ButtonText>
              </Button>
              <Button
                variant="solid"
                type="submit"
                bgColor="secondary.500"
                w="100%"
                h={9}
                borderRadius={4}
                _hover={{
                  bgColor: "secondary.300",
                }}
                _active={{
                  bgColor: "secondary.500",
                }}
              >
                <Fonts.ButtonText>Confirmar</Fonts.ButtonText>
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {isLoading && <LoadingOverlay />}
      {!isLoading && (
        <Stack p={3} w="100%" mt="0 !important">
          <TableContainer w="100%">
            <Table>
              <Thead bgColor="neutral.300">
                <Tr>
                  <Th textAlign="center" width="35%">
                    <Fonts.TableTheadText>Nome</Fonts.TableTheadText>
                  </Th>
                  <Th textAlign="center">
                    <Fonts.TableTheadText>Tipo</Fonts.TableTheadText>
                  </Th>
                  <Th textAlign="center" w="15%">
                    <Fonts.TableTheadText>Ações</Fonts.TableTheadText>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {parametrizations.map((param) => (
                  <T.Body key={param.id}>
                    <Td textAlign="center" width="35%">
                      <Fonts.TableBodyText>{param.name}</Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center">
                      <Fonts.TableBodyText>
                        {getTypeLabel(param.type)}
                      </Fonts.TableBodyText>
                    </Td>
                    <Td textAlign="center" w="15%">
                      <Tooltip label="Editar">
                        <Button
                          variant="ghost"
                          onClick={() => handleEdit(param)}
                        >
                          <OutlineEditNote color="blue" />
                        </Button>
                      </Tooltip>
                      <Tooltip label="Excluir">
                        <Button
                          variant="ghost"
                          onClick={() => handleDelete(param.id)}
                        >
                          <TrashOutline color="blue" />
                        </Button>
                      </Tooltip>
                    </Td>
                  </T.Body>
                ))}
              </Tbody>
              <Tfoot w="100%"></Tfoot>
            </Table>
            {parametrizations.length && (
              <Pagination page={page} lastPage={lastPage} onClick={setPage} />
            )}
          </TableContainer>
        </Stack>
      )}
    </MainContainer>
  );
};
